import { Box, ButtonNew, Text } from '@jfs/ui-new'
import * as React from 'react'
import { ReactElement } from 'react'
import { Dimensions, FlatList, NativeScrollEvent, NativeSyntheticEvent, ViewStyle } from 'react-native'
import type { StyleProp } from 'react-native'
import { ModBox } from '../../../comps/ModifiedBox/ModifiedBox'
import { ListItemRefunds, ListItemSettlement, ListItemTxn } from '../ListRow'
import { styles } from '../ListRow/styles'

export type ListRenderItem<ItemT> = (info: ListRenderItemInfo<ItemT>) => React.ReactElement | null
export interface ListRenderItemInfo<ItemT> {
  item: ItemT
  index: number
  separators: {
    highlight: () => void
    unhighlight: () => void
    updateProps: (select: 'leading' | 'trailing', newProps: any) => void
  }
}

const LocalKeyExtractor = (item: any): string => {
  return item.id
}

interface ListProps {
  data: Array<any>
  disabled: boolean
  onPress: (item: any, index: number) => void
  scrollEnabled?: boolean
  onListFooterPress?: () => void
  isSeeAllVisible?: boolean
  type: string
  loadingState: boolean
  canFetchMore: boolean
  isCustom?: boolean
  RenderCustomElement?: ListRenderItem<any> | null | undefined
  keyExtractor?: (item: any, index: number) => string
  ItemSeparatorComponent?: React.ComponentType<any> | null
  listKey?: string
  customStyle?: StyleProp<ViewStyle>
  testID?: string
  keyboardDismissMode?: 'none' | 'interactive' | 'on-drag'
  keyboardShouldPersistTaps?: boolean | 'always' | 'never' | 'handled'
  extraData?: any
  ListHeaderComponent?: React.ComponentType<any> | React.ReactElement | null
  ListEmptyComponent?: React.ComponentType<any> | React.ReactElement | null
  contentContainerStyle?: StyleProp<ViewStyle>
  numColumns?: number
  horizontal?: boolean
  columnWrapperStyle?: StyleProp<ViewStyle>
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void
  decelerationRate?: 'fast' | 'normal' | number
  disableIntervalMomentum?: boolean
  pagingEnabled?: boolean
  snapToOffsets?: number[]
  getItemLayout?: (
    data: Array<any> | null | undefined,
    index: number,
  ) => { length: number; offset: number; index: number }
  listRef?: any
  onMomentumScrollEnd?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void
  overScrollMode?: 'auto' | 'always' | 'never'
  accessibilityLabel?: string
  onEndReached?: ((info: { distanceFromEnd: number }) => void) | null
  onEndReachedThreshold?: number | null
  stickyHeaderIndices?: number[]
  refreshControl?: any
  onExport?: any
  isReportDownloading?: boolean
  selectedIdForDownload?: any
  onScrollBeginDrag?: () => void
  onScrollEndDrag?: () => void
  snapToInterval?: number
  snapToAlignment?: string
}

const List = ({
  data,
  onPress,
  disabled,
  scrollEnabled = true,
  onListFooterPress,
  isSeeAllVisible,
  type,
  loadingState,
  canFetchMore,
  isCustom,
  RenderCustomElement,
  keyExtractor,
  ItemSeparatorComponent,
  listKey,
  customStyle,
  testID,
  keyboardDismissMode,
  keyboardShouldPersistTaps,
  extraData,
  ListHeaderComponent,
  ListEmptyComponent,
  contentContainerStyle,
  numColumns,
  horizontal,
  columnWrapperStyle,
  onScroll,
  decelerationRate,
  disableIntervalMomentum,
  pagingEnabled,
  snapToOffsets,
  getItemLayout,
  listRef,
  onMomentumScrollEnd,
  overScrollMode,
  onEndReached,
  accessibilityLabel,
  onEndReachedThreshold,
  stickyHeaderIndices,
  refreshControl,
  onExport,
  isReportDownloading,
  selectedIdForDownload,
  onScrollEndDrag,
  onScrollBeginDrag,
  snapToInterval,
  snapToAlignment,
}: ListProps) => {
  const RenderItem = (propsData: any): ReactElement => {
    const width = Dimensions.get('screen').width
    if (type === 'refunds') {
      return (
        <ListItemRefunds
          disabled={disabled}
          onPress={() => onPress(propsData.item, propsData.index)}
          item={propsData.item}
          index={propsData.index}
          width={width}
        />
      )
    }
    if (type === 'transactions') {
      return (
        <ListItemTxn
          disabled={disabled}
          onPress={() => onPress(propsData.item, propsData.index)}
          item={propsData.item}
          index={propsData.index}
          width={width}
        />
      )
    }
    return (
      <ListItemSettlement
        disabled={disabled}
        onPress={() => onPress(propsData.item, propsData.index)}
        item={propsData.item}
        index={propsData.index}
        onExport={onExport}
        isReportDownloading={isReportDownloading}
        selectedIdForDownload={selectedIdForDownload}
      />
    )
  }

  const renderListFooterComponent = () => {
    if ((data?.length > 1 && isSeeAllVisible) ?? false) {
      return (
        <ModBox
          show={(data?.length > 1 && isSeeAllVisible) ?? false}
          onPress={onListFooterPress}
          testID={'view-all-txn'}
          {...styles.listFooter}
          underlayColor="WHITE">
          <Text type="h3Bold" color={'BLUE'}>
            {'View All Transactions'}
          </Text>
        </ModBox>
      )
    }
    if (data?.length > 1 && !isSeeAllVisible && canFetchMore) {
      return (
        <Box pv={24} align={'center'} alignSelf={'center'}>
          <ButtonNew
            title={'Show more'}
            onPress={onListFooterPress}
            testID={'btn-show-more'}
            type={'secondary'}
            titleColor={'PRIMARY'}
            paddingHorizontal={24}
            borderColor={'PRIMARY'}
            isRaceTrack
          />
        </Box>
      )
    }
    return <></>
  }
  return (
    <FlatList
      showsVerticalScrollIndicator={false}
      keyExtractor={keyExtractor ?? LocalKeyExtractor}
      data={data}
      extraData={extraData ?? data}
      scrollEnabled={scrollEnabled}
      keyboardDismissMode={keyboardDismissMode && keyboardDismissMode}
      keyboardShouldPersistTaps={keyboardShouldPersistTaps ?? 'handled'}
      overScrollMode={overScrollMode ?? 'always'}
      renderItem={isCustom && RenderCustomElement ? RenderCustomElement : RenderItem}
      ListFooterComponent={renderListFooterComponent}
      ItemSeparatorComponent={ItemSeparatorComponent && ItemSeparatorComponent}
      listKey={listKey}
      style={customStyle && customStyle}
      testID={testID}
      showsHorizontalScrollIndicator={false}
      ListHeaderComponent={ListHeaderComponent}
      ListEmptyComponent={ListEmptyComponent}
      contentContainerStyle={contentContainerStyle}
      numColumns={numColumns}
      initialNumToRender={10}
      maxToRenderPerBatch={3}
      horizontal={horizontal}
      columnWrapperStyle={columnWrapperStyle}
      onScroll={onScroll}
      decelerationRate={decelerationRate}
      disableIntervalMomentum={disableIntervalMomentum}
      pagingEnabled={pagingEnabled}
      snapToOffsets={snapToOffsets}
      getItemLayout={getItemLayout}
      ref={listRef}
      onMomentumScrollEnd={onMomentumScrollEnd}
      accessibilityLabel={accessibilityLabel}
      onEndReached={onEndReached}
      onEndReachedThreshold={onEndReachedThreshold}
      updateCellsBatchingPeriod={5}
      stickyHeaderIndices={stickyHeaderIndices}
      refreshControl={refreshControl}
      removeClippedSubviews={false}
      onScrollBeginDrag={onScrollBeginDrag}
      onScrollEndDrag={onScrollEndDrag}
      snapToInterval={snapToInterval}
      snapToAlignment={snapToAlignment}
    />
  )
}

export { List }
